import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Kanatal = () => {
  const tourData = {
    name: "Kanatal Tehri Lake",
    duration: "02 Nights 03 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755005553/zw0w4nvg3ygkkjar3fjw.jpg",
    altitude: "8,500 ft",
    difficulty: "Easy",
    region: "Uttarakhand",
    highlights: ["Weekend getaway", "Adventure activities", "Mountain views"],
    bestTime: "March to November",
    description: "The Kanatal Tehri Lake tour is a perfect weekend getaway in Uttarakhand that combines adventure activities with scenic beauty. This easy tour offers water sports, temple visits, and mountain views, making it ideal for families and adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Kanatal Tehri Lake Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Kanatal Tehri Lake tour in Uttarakhand. Weekend getaway with adventure activities and scenic beauty." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                filter: 'brightness(0.8) contrast(1.2) saturate(1.1)'
              }}
            />
            <div className="hero-overlay" style={{
              background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)'
            }}></div>
          </div>
          <div className="hero-content" style={{
            textAlign: 'center',
            zIndex: 2,
            position: 'relative'
          }}>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{
                fontSize: '3.5rem',
                fontWeight: '700',
                color: '#ffffff',
                textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                marginBottom: '1rem',
                letterSpacing: '0.5px'
              }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{
                fontSize: '1.3rem',
                color: '#ffffff',
                textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
                fontWeight: '400',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}
            >
              Weekend getaway with adventure activities and scenic beauty
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    marginBottom: '2rem'
                  }}
                >
                  <h2 style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '1.5rem',
                    borderBottom: '3px solid #2563eb',
                    paddingBottom: '0.5rem'
                  }}>Tour Overview</h2>
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#4b5563',
                    lineHeight: '1.7',
                    marginBottom: '1.5rem',
                    fontWeight: '400'
                  }}>{tourData.description}</p>
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#4b5563',
                    lineHeight: '1.7',
                    fontWeight: '400'
                  }}>
                    This tour takes you to the beautiful hill station of Kanatal and the 
                    stunning Tehri Lake, offering a perfect blend of adventure and relaxation. 
                    The journey includes temple visits, water sports activities, and scenic 
                    mountain views, making it an ideal weekend escape from city life.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    marginBottom: '2rem'
                  }}
                >
                  <h3 style={{
                    fontSize: '1.8rem',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '1.5rem',
                    borderBottom: '3px solid #2563eb',
                    paddingBottom: '0.5rem'
                  }}>Highlights</h3>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {[
                      'Weekend getaway experience',
                      'Adventure activities',
                      'Mountain views',
                      'Water sports at Tehri Lake',
                      'Surkanda Devi Temple visit',
                      'Easy difficulty level',
                      'Perfect for families',
                      'Bonfire and music'
                    ].map((highlight, index) => (
                      <li key={index} style={{
                        fontSize: '1.1rem',
                        color: '#4b5563',
                        lineHeight: '1.7',
                        marginBottom: '0.8rem',
                        paddingLeft: '1.5rem',
                        position: 'relative',
                        fontWeight: '400'
                      }}>
                        <span style={{
                          position: 'absolute',
                          left: 0,
                          top: '0.3rem',
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#2563eb',
                          borderRadius: '50%'
                        }}></span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Departure for Kanatal</h4>
                    <ul>
                      <li>Departure from Delhi at night</li>
                      <li>Overnight Journey from Delhi to Kanatal</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Reach Kanatal & Visit Surkanda Devi Temple</h4>
                    <ul>
                      <li>Reach Kanatal & have breakfast</li>
                      <li>After check-in into rooms, get fresh and up for Adventure Activities</li>
                      <li>Visit to Dhanaulti Eco Park & Surkanda Devi Mandir</li>
                      <li>Back to property & enjoy evening snacks and tea</li>
                      <li>Later enjoy dinner at night with bonfire and light music</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Tehri Lake (Water Sports Activities)</h4>
                    <ul>
                      <li>Wake up early morning and enjoy the beautiful morning view of KANATAL</li>
                      <li>Refresh & have breakfast</li>
                      <li>After Breakfast we will start our journey to Tehri lake for adventure activities</li>
                      <li>It will take around 1 hour drive to tehri lake</li>
                      <li>We can enjoy various adventure activities there like Jet Skiing, Banana ride, jet attack, Speed boat and much more on direct payment basis</li>
                      <li>After leaving tehri lake, we will depart for Rishikesh for Ganga Aarti (depending upon time) and local sightseeing in rishikesh</li>
                      <li>We will reach Delhi by Early Morning</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Travel by AC & Comfortable Bus/Traveler</li>
                    <li>1 Night at Kanatal Camp-site</li>
                    <li>Meal: 1 Breakfast, 1 Dinner</li>
                    <li>Surkhanda Devi temple</li>
                    <li>Tehri Lake Visit for Water sports activities</li>
                    <li>Adventure Activities - Zip-line, Cycling (on your own expense)</li>
                    <li>Trip Coordinator</li>
                    <li>24x7 Customer Support</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>5% GST</li>
                    <li>Extra Drinks, Food, Tea and Snacks</li>
                    <li>Tickets for any sightseeing or extra activity outside the camp area</li>
                    <li>Travel Insurance and other benefits</li>
                    <li>Medical expenses (apart from first aid) and insurance of any kind</li>
                    <li>Tips, laundry, liquors, wines, mineral water, telephone charges, camera fee and items of personal nature</li>
                    <li>Anything which is not mentioned in inclusions</li>
                    <li>Cost escalation due any unforeseen reason like landslide, road closure, natural calamity</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>8,500 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>300 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>QUAD SHARING:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Weekend getaway experience</li>
                    <li>Adventure activities</li>
                    <li>Mountain views</li>
                    <li>Water sports at Tehri Lake</li>
                    <li>Surkanda Devi Temple visit</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Bonfire and music</li>
                    <li>Trip coordinator support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Kanatal;
